import { FileText, Loader2, MoreVertical } from "lucide-react";
import { motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { WorkspacePanelHeading } from "@/components/workspace/workspace-panel-heading";
import { WorkspaceToolCards } from "@/components/workspace/workspace-tool-cards";
import { useTranslations } from "@/hooks/use-translations";
import { canvasCardLabel, type ToolResult } from "@/lib/api";

function formatTimeAgo(dateStr: string): string {
	const diff = Date.now() - new Date(dateStr).getTime();
	const mins = Math.floor(diff / 60000);
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	const days = Math.floor(hrs / 24);
	return `${days}d ago`;
}

interface WorkspaceStudioPanelProps {
	loadingTool: string | null;
	onRunTool: (toolSlug: string | null) => void;
	onOpenContentPlan: () => void;
	toolResults: ToolResult[];
	isLoadingResults: boolean;
	onOpenResult: () => void;
}

/** Right column: Studio tool cards and the history of their results. */
export function WorkspaceStudioPanel({
	loadingTool,
	onRunTool,
	onOpenContentPlan,
	toolResults,
	isLoadingResults,
	onOpenResult,
}: WorkspaceStudioPanelProps) {
	const t = useTranslations("workspace");

	return (
		<motion.aside
			initial={{ opacity: 0, x: 20 }}
			animate={{ opacity: 1, x: 0 }}
			transition={{ duration: 0.3, delay: 0.15 }}
			className="w-[300px] 2xl:w-[360px] flex-shrink-0 border border-t-0 border-l-0 rounded-none border-border-subtle bg-bg-surface flex flex-col"
		>
			<WorkspacePanelHeading title={t("studio")} />

			<WorkspaceToolCards
				loadingTool={loadingTool}
				onRunTool={onRunTool}
				onOpenContentPlan={onOpenContentPlan}
			/>

			{/* Tool Results History — independently scrollable */}
			<div className="flex-1 overflow-y-auto px-4 pb-2 scrollbar-thin border-t border-border-subtle">
				<div className="space-y-1 pt-3">
					{isLoadingResults ? (
						<div className="flex items-center justify-center py-4">
							<Loader2 size={16} className="animate-spin text-text-tertiary" />
						</div>
					) : toolResults.length === 0 ? (
						<p className="text-xs text-text-tertiary text-center py-4">
							No results yet
						</p>
					) : (
						toolResults
							.filter((item) => !!item.content)
							.map((item, itemIndex) => (
								<div
									key={item.id}
									onClick={onOpenResult}
									className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-bg-canvas transition-colors group cursor-pointer"
								>
									<div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
										<FileText size={14} className="text-text-primary" />
									</div>
									<div className="flex-1 min-w-0">
										<p className="text-xs font-medium text-text-primary truncate">
											{canvasCardLabel(item, toolResults, itemIndex)}
										</p>
										<p className="text-2xs text-text-tertiary">
											{formatTimeAgo(item.createdAt)}
										</p>
									</div>
									<Button variant="ghost" size="icon-xs">
										<MoreVertical size={14} />
									</Button>
								</div>
							))
					)}
				</div>
			</div>
		</motion.aside>
	);
}
